import {
  ShowSection,
  BigSectionRow,
  SectionRow,
  FieldGroup,
  LabeledFieldLayout,
  ShowPageLayout,
} from 'framework/ui/layout';
import { WasteHeader, RedmineIssueListField } from 'domain/shared/waste';
import SingleWasteBreadcrumbsItem from './SingleWasteBreadcrumbsItem';
import { DateField, EnumField, TextField } from 'framework/ui/field';
import {
  WASTE_REPORTING_METHOD,
  WASTE_REPORTING_METHOD_OPTIONS,
  WASTE_STATUSES,
  WASTE_STATUSES_OPTIONS,
  WASTE_TYPE,
  WASTE_TYPE_OPTIONS,
} from 'domain/shared/waste';
import { useShowContext } from 'framework/show';
import { InvoiceTotalField } from 'domain/shared/core/invoice';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { LedgerLink } from 'domain/shared/ledger';
import ApproveWasteButton from '../shared/button/ApproveWasteButton';
import RejectWasteButton from '../shared/button/RejectWasteButton';
import ProjectField from 'infrastructure/integrations/redmine/ProjectField';

const ViewWastePageContent = () => {
  const data = useShowContext()?.data;

  const redmineIssueIds = data?.getValueBySource('laborWasteDetails.ticketsIds') as number[];
  const isLaborCostWaste = data?.getValueBySource('type') === WASTE_TYPE.LABOR_COST_WASTE;
  const isReportingMethodByTicketAndEmployee =
    data?.getValueBySource('laborWasteDetails.reportingMethod') === WASTE_REPORTING_METHOD.BY_EMPLOYEE_AND_TICKET_IDS;
  const isReportingMethodByTicket =
    data?.getValueBySource('laborWasteDetails.reportingMethod') === WASTE_REPORTING_METHOD.BY_TICKET_IDS;

  return (
    <ShowPageLayout
      entityHeader={<WasteHeader />}
      breadcrumbs={<SingleWasteBreadcrumbsItem />}
      headerButtons={[<ApproveWasteButton statusSource="status" />, <RejectWasteButton statusSource="status" />]}
    >
      <ShowSection title="Main information">
        <SectionRow>
          <EnumField label="Type" source="type" options={WASTE_TYPE_OPTIONS} />
          <EnumField
            isVisible={isLaborCostWaste}
            label="Reporting Method"
            source="laborWasteDetails.reportingMethod"
            options={WASTE_REPORTING_METHOD_OPTIONS}
          />
          <EnumField label="Status" source="status" options={WASTE_STATUSES_OPTIONS} />
        </SectionRow>

        <SectionRow>
          <InvoiceTotalField
            label="Amount"
            source="otherWasteDetails.cost.amount"
            currencySource="otherWasteDetails.cost.currency"
            isVisible={!isLaborCostWaste}
          />
          <DateField isVisible={!isLaborCostWaste} label="Issue Date" source="otherWasteDetails.issueDate" />

          <EmployeeLinkFieldWithNameLoading
            label="Worker"
            source="laborWasteDetails.employeeAlias"
            isVisible={isLaborCostWaste && !isReportingMethodByTicket}
          />

          <FieldGroup
            isVisible={(isReportingMethodByTicket || isReportingMethodByTicketAndEmployee) && isLaborCostWaste}
          >
            <LabeledFieldLayout
              value={redmineIssueIds?.map((issueId: number) => {
                return <RedmineIssueListField key={issueId} id={issueId} />;
              })}
              label="Redmine ticket ID (s)"
            />
          </FieldGroup>

          <DateField
            isVisible={!isReportingMethodByTicket && !isReportingMethodByTicketAndEmployee && isLaborCostWaste}
            label="Period from"
            source="laborWasteDetails.period.startDate"
          />
          <DateField
            isVisible={!isReportingMethodByTicket && !isReportingMethodByTicketAndEmployee && isLaborCostWaste}
            label="Period to"
            source="laborWasteDetails.period.endDate"
          />
          <ProjectField
            isVisible={!isReportingMethodByTicket && !isReportingMethodByTicketAndEmployee && isLaborCostWaste}
            source="laborWasteDetails.projectId"
            nameSource="laborWasteDetails.projectId"
            label="Project"
          />
        </SectionRow>

        <BigSectionRow>
          <TextField label="Reason" source="reason" />
        </BigSectionRow>
      </ShowSection>

      <ShowSection title="Approval Details" isVisible={data.getValueBySource('status') === WASTE_STATUSES.APPROVED}>
        <SectionRow>
          <LedgerLink
            source="approvalDetails.attribution.sourceAccountId"
            nameSource="sourceAccountId"
            label="Source Account"
          />
          <TextField
            source="approvalDetails.attribution.sourceBudgetCategory"
            nameSource="sourceBudgetCategory"
            label="Source Budget Category"
          />
          <LedgerLink
            source="approvalDetails.attribution.targetAccountId"
            nameSource="targetAccountId"
            label="Target Account"
          />
          <TextField
            source="approvalDetails.attribution.targetBudgetCategory"
            nameSource="targetBudgetCategory"
            label="Target Budget Category"
          />
          <EmployeeLinkFieldWithNameLoading label="Approved By" source="approvalDetails.approvedBy" />
        </SectionRow>
      </ShowSection>
    </ShowPageLayout>
  );
};

export default ViewWastePageContent;
